/**
 * What each blocking screen says, as a list a thumbstick can walk.
 *
 * These mirror `ui/menus.ts`, `ui/inventory.ts` and `ui/notes.ts` — same words,
 * same order, same armed confirmations on the two rows that end a run — and
 * they reach the game through the identical `UIAction`s, so a run started from
 * a headset is indistinguishable from one started with a mouse.
 *
 * The settings screen is the one deliberate divergence, and the comment on
 * `settingsScreen` says why.
 */

import type { JournalEntry, ResumeOffer, ScreenName, UIAction } from '../../core/systems';
import type { ComfortHost, GameSettings, InventorySlot, RenderSettings, XRComfort } from '../../core/types';
import { fmtTime } from '../dom';
import { ITEM_UI } from '../items';
import type { MenuModel, MenuRow } from './menu';

/** Phrasing for a death. Restated from `CAUSE_LINES` in `ui/menus.ts`. */
const CAUSE_LINES: ReadonlyArray<readonly [RegExp, string]> = [
  [/hound/i, 'Something fast found you.'],
  [/smiler/i, 'You looked at it for too long.'],
  [/stealer|stalk/i, 'It had been walking behind you for a while.'],
  [/wretch/i, 'It got hold of you in a corridor.'],
  [/clump/i, 'You touched the wrong wall.'],
  [/fall/i, 'The floor was not where you left it.'],
  [/^player$/i, 'Someone you could see did that.'],
];

/**
 * How the player moves, in the words `render/xrteleport.ts` uses for it: a dash
 * is the hold-to-hop teleport, an arc is point-and-blink, smooth is the stick.
 *
 * Teleport is the default and smooth is what you opt into, which is why this
 * row exists at all — and why it is first in the comfort list.
 */
const MOVE_STYLES = ['dash', 'arc', 'smooth'] as const;

/**
 * A movement style from the row's own option list, narrowed back to the field's
 * type. The menu deals in strings, and this is the one place that has to turn
 * one back into a setting.
 */
function asMoveStyle(v: string): XRComfort['move'] {
  return v === 'arc' || v === 'smooth' ? v : 'dash';
}

export interface DeathResult {
  reason: string;
  survived: number;
  depth: number;
  /** Already in the game's words: `Level 2 — The Pipes`. */
  place: string;
}

/**
 * Everything a screen is built from.
 *
 * The state is exposed as getters by `layer.ts`, and is therefore `readonly`
 * here: a row's `get()` is called when it is drawn and again when it is
 * pressed, both long after the model was built, so a model that had captured
 * copies would show the settings as they were when the screen opened.
 */
export interface ScreenDeps {
  dispatch(a: UIAction): void;
  show(screen: ScreenName): void;
  openSettings(): void;
  closeSettings(): void;
  openJournal(): void;
  closeJournal(): void;

  readonly settings: GameSettings;
  /** Applies an edit and tells the game, exactly as the DOM controls do. */
  applySettings(s: GameSettings): void;
  /**
   * Comfort. Persists itself on every write and is never part of the save
   * blob, so unlike `settings` there is nothing to apply — the setter is the
   * whole of it. See `core/types.ts`.
   */
  readonly comfort: ComfortHost;

  readonly resume: ResumeOffer | null;
  readonly journal: readonly JournalEntry[];
  readonly journalIndex: number;
  selectJournal(i: number): void;
  readonly inventory: readonly InventorySlot[];
  readonly death: DeathResult | null;

  /**
   * Open and close the comfort panel — the short list reachable from the plate
   * in the corner of the eye, and from the pause screen. See `comfortScreen`.
   */
  openQuick(): void;
  closeQuick(): void;
  /**
   * Leave the comfort panel for the full pause menu underneath it. The only
   * route to a pause menu at all on a headset with no buttons to open one.
   */
  showMenu(): void;
  /** True when closing the comfort panel means going back to playing. */
  readonly quickFromPlay: boolean;
}

/** The model for a screen, or null for the ones that are not a menu. */
export function buildScreen(name: ScreenName, d: ScreenDeps): MenuModel | null {
  switch (name) {
    case 'title':
      return titleScreen(d);
    case 'paused':
      return pausedScreen(d);
    case 'settings':
      return settingsScreen(d);
    case 'dead':
      return deadScreen(d);
    case 'inventory':
      return inventoryScreen(d);
    case 'journal':
      return journalScreen(d);
    default:
      // 'hud' and 'levelcard' are play, not a menu.
      return null;
  }
}

/* ------------------------------------------------------------------ *
 * Title
 * ------------------------------------------------------------------ */

function titleScreen(d: ScreenDeps): MenuModel {
  const rows: MenuRow[] = [];
  // The offer to go back leads, as it does on the DOM title. There is no seed
  // field: typing one needs a keyboard, and a headset does not have one worth
  // using. A player who wants a specific building starts the run at a desk.
  if (d.resume) {
    rows.push({ kind: 'action', label: 'CONTINUE', run: () => d.dispatch({ type: 'continue' }) });
  }
  rows.push({ kind: 'action', label: 'ENTER', run: () => d.dispatch({ type: 'start' }) });
  rows.push({ kind: 'action', label: 'SETTINGS', run: () => d.openSettings() });

  return {
    key: 'title',
    title: 'THE BACKROOMS',
    sub: d.resume ? describeResume(d.resume) : 'Six hundred million square miles of damp carpet.',
    rows,
  };
}

function describeResume(offer: ResumeOffer): string {
  const ago = Math.max(0, Date.now() - offer.savedAt);
  const minutes = Math.round(ago / 60_000);
  const when = minutes < 1 ? 'MOMENTS AGO' : minutes < 60 ? `${minutes} MINUTES AGO` : `${Math.round(minutes / 60)} HOURS AGO`;
  return `${offer.place.toUpperCase()} · ${when}`;
}

/* ------------------------------------------------------------------ *
 * Pause
 * ------------------------------------------------------------------ */

function pausedScreen(d: ScreenDeps): MenuModel {
  return {
    key: 'paused',
    title: 'PAUSED',
    sub: 'The hum does not stop while you are away.',
    rows: [
      { kind: 'action', label: 'RESUME', run: () => d.dispatch({ type: 'resume' }) },
      // Second, above SETTINGS, so comfort is one press from the pause screen
      // as well as one press from the plate in the corner of the eye. Somebody
      // who needs teleport needs it now, not after reading a settings list.
      { kind: 'action', label: 'COMFORT', run: () => d.openQuick() },
      // The pack is on the DOM screen's Tab key and on a controller's A button,
      // and a headset with neither — a Vision Pro has no buttons at all — would
      // otherwise have no way into it for a whole run. The bandages are in
      // there. This is the second deliberate divergence from `ui/menus.ts`.
      { kind: 'action', label: 'PACK', run: () => d.show('inventory') },
      // And the third and fourth, for the same reason and a worse consequence.
      // The torch is bound to X and the map to Y, and a runtime whose only
      // input is a pinch has neither — so on a Vision Pro, before these rows,
      // a player could find a flashlight and never be able to switch it on.
      // This game is played in the dark. Both put the player back in the
      // building immediately, because that is the only reason to press either.
      { kind: 'action', label: 'TORCH', run: () => d.dispatch({ type: 'torch' }) },
      { kind: 'action', label: 'MAP', run: () => d.dispatch({ type: 'map' }) },
      { kind: 'action', label: 'SETTINGS', run: () => d.openSettings() },
      { kind: 'action', label: 'FIELD NOTES', run: () => d.openJournal() },
      { kind: 'action', label: 'RESTART', confirm: 'RESTART — CONFIRM', run: () => d.dispatch({ type: 'restart' }) },
      { kind: 'action', label: 'QUIT TO TITLE', confirm: 'QUIT — CONFIRM', run: () => d.dispatch({ type: 'quit' }) },
    ],
  };
}

/* ------------------------------------------------------------------ *
 * Settings
 * ------------------------------------------------------------------ */

/**
 * A shorter list than the DOM's, on purpose.
 *
 * Four of the desktop rows cannot mean anything in a headset and three of them
 * would be actively harmful if they did: SENSITIVITY and INVERT Y describe a
 * mouse, and FIELD OF VIEW is decided by the lenses, not by us — writing to it
 * inside a session would either be ignored or would break stereo. They are left
 * on the desktop screen where they belong rather than shown here doing nothing.
 *
 * HEAD BOB stays because it is the one comfort setting that matters more in VR
 * than out of it. POST-FX is left off deliberately: whether the post chain even
 * runs in stereo is the renderer's call this batch, and a toggle that might be
 * overruled is worse than no toggle.
 */
function settingsScreen(d: ScreenDeps): MenuModel {
  const edit = (fn: (s: GameSettings) => void): void => {
    const next: GameSettings = { ...d.settings, render: { ...d.settings.render } };
    fn(next);
    d.applySettings(next);
  };

  return {
    key: 'settings',
    title: 'SETTINGS',
    sub: 'Changes apply immediately and are saved.',
    rows: [
      {
        kind: 'range',
        label: 'MASTER VOLUME',
        min: 0,
        max: 1,
        step: 0.05,
        get: () => d.settings.masterVolume,
        set: (v) => edit((s) => (s.masterVolume = v)),
        format: (v) => `${Math.round(v * 100)}%`,
      },
      { kind: 'toggle', label: 'MUTE', get: () => d.settings.muted, set: (v) => edit((s) => (s.muted = v)) },
      {
        kind: 'toggle',
        label: 'SUBTITLES',
        get: () => d.settings.subtitles,
        set: (v) => edit((s) => (s.subtitles = v)),
      },
      {
        kind: 'choice',
        label: 'QUALITY',
        options: ['low', 'medium', 'high'],
        get: () => d.settings.render.quality,
        set: (v) => edit((s) => (s.render.quality = v as RenderSettings['quality'])),
      },
      {
        kind: 'range',
        label: 'RESOLUTION',
        min: 0.5,
        max: 1,
        step: 0.05,
        get: () => d.settings.render.resolutionScale,
        set: (v) => edit((s) => (s.render.resolutionScale = v)),
        format: (v) => `${Math.round(v * 100)}%`,
      },
      {
        kind: 'toggle',
        label: 'HEAD BOB',
        get: () => d.settings.render.headBob,
        set: (v) => edit((s) => (s.render.headBob = v)),
      },
      ...comfortRows(d),
      { kind: 'action', label: 'BACK', run: () => d.closeSettings() },
    ],
  };
}

/**
 * Comfort, which is the half of this screen that only exists in here.
 *
 * It is on the in-world settings screen and not on the DOM one on purpose: a
 * player deciding whether 30° per snap is too much or not enough has to be
 * wearing the thing while they decide, and a row about snap turning on a
 * monitor is noise for the overwhelming majority of players who will never see
 * a headset. Every one of these writes straight through and persists itself;
 * there is no apply and no save to wait for.
 *
 * SNAP DEGREES and SMOOTH DEGREES are both always listed rather than swapped
 * for each other, because a list whose length changes under the cursor moves
 * the selection out from under the player mid-press.
 */
function comfortRows(d: ScreenDeps): readonly MenuRow[] {
  const c = d.comfort;
  const set = (patch: Partial<XRComfort>): void => c.setComfort(patch);
  return [
    { kind: 'info', label: 'COMFORT' },
    ...comfortControls(d),
    {
      kind: 'choice',
      label: 'MOVE STICK',
      options: ['left', 'right'],
      get: () => c.comfort.moveHand,
      set: (v) => set({ moveHand: v === 'right' ? 'right' : 'left' }),
    },
    {
      kind: 'choice',
      label: 'TORCH HAND',
      options: ['left', 'right'],
      get: () => c.comfort.torchHand,
      set: (v) => set({ torchHand: v === 'right' ? 'right' : 'left' }),
    },
  ];
}

/**
 * The four or five rows that decide whether somebody can wear this at all,
 * built once and used by both comfort lists.
 *
 * MOVEMENT is first because it is the one that matters most and the one people
 * come looking for: it is the difference between hopping through the building
 * and sliding through it, and sliding through it is what makes people ill.
 *
 * MOVE STICK and TORCH HAND are deliberately *not* in here. They are
 * preferences, they take a while to decide, and the short list is for the
 * ninety seconds in which somebody has realised they feel ill.
 */
function comfortControls(d: ScreenDeps): MenuRow[] {
  const c = d.comfort;
  const set = (patch: Partial<XRComfort>): void => c.setComfort(patch);
  const rows: MenuRow[] = [];

  rows.push(
    {
      kind: 'choice',
      label: 'MOVEMENT',
      options: MOVE_STYLES,
      get: () => c.comfort.move,
      set: (v) => set({ move: asMoveStyle(v) }),
    },
    {
      kind: 'choice',
      label: 'TURNING',
      options: ['snap', 'smooth'],
      get: () => c.comfort.turn,
      set: (v) => set({ turn: v === 'smooth' ? 'smooth' : 'snap' }),
    },
    {
      kind: 'range',
      label: 'SNAP ANGLE',
      min: 10,
      max: 90,
      step: 5,
      get: () => c.comfort.snapDegrees,
      set: (v) => set({ snapDegrees: v }),
      format: (v) => `${Math.round(v)}\u00b0`,
    },
    {
      kind: 'range',
      label: 'SMOOTH SPEED',
      min: 30,
      max: 220,
      step: 10,
      get: () => c.comfort.smoothDegrees,
      set: (v) => set({ smoothDegrees: v }),
      format: (v) => `${Math.round(v)}\u00b0/s`,
    },
    {
      kind: 'range',
      label: 'TUNNEL',
      min: 0,
      max: 1,
      step: 0.1,
      get: () => c.comfort.vignette,
      set: (v) => set({ vignette: v }),
      format: (v) => (v <= 0 ? 'OFF' : `${Math.round(v * 100)}%`),
    },
  );
  return rows;
}

/* ------------------------------------------------------------------ *
 * Comfort, on its own
 * ------------------------------------------------------------------ */

/**
 * The short list, two presses from anywhere.
 *
 * This is the screen the brief is really about. Somebody works out that they
 * need teleport by starting to feel ill, and at that point every extra step is
 * a step taken while feeling ill: pinch the plate in the corner of the eye,
 * pinch MOVEMENT. It is deliberately short enough never to scroll, so nothing
 * here can be off the bottom of the panel when it is wanted.
 *
 * Opening it stops the building \u2014 `layer.ts` shows the pause screen underneath
 * \u2014 because the run continuing while somebody reads a menu is exactly the
 * situation that made them ill in the first place.
 */
export function comfortScreen(d: ScreenDeps): MenuModel {
  const rows: MenuRow[] = [...comfortControls(d)];
  // Only on the way in from play. Opened from the pause screen this row and
  // BACK would go to the same place, and two rows that do one thing is how a
  // menu stops being readable at a glance.
  if (d.quickFromPlay) {
    rows.push({ kind: 'action', label: 'THE REST OF THE MENU', run: () => d.showMenu() });
  }
  rows.push({
    kind: 'action',
    label: d.quickFromPlay ? 'BACK TO THE FLOOR' : 'BACK',
    run: () => d.closeQuick(),
  });
  return {
    key: 'comfort',
    title: 'COMFORT',
    sub: 'SAVED AS YOU CHANGE THEM.',
    rows,
  };
}

/* ------------------------------------------------------------------ *
 * Death
 * ------------------------------------------------------------------ */

function deadScreen(d: ScreenDeps): MenuModel {
  const death = d.death;
  const match = death ? CAUSE_LINES.find(([re]) => re.test(death.reason)) : undefined;
  return {
    key: 'dead',
    title: 'THE HUM CONTINUES',
    sub: 'END OF RUN',
    body: [match ? match[1] : death?.reason || 'You stopped.'],
    rows: [
      { kind: 'info', label: 'SURVIVED', value: fmtTime(death?.survived ?? 0) },
      { kind: 'info', label: 'REACHED', value: death?.place ?? '—' },
      { kind: 'action', label: 'AGAIN', run: () => d.dispatch({ type: 'restart' }) },
      { kind: 'action', label: 'TITLE', run: () => d.dispatch({ type: 'quit' }) },
    ],
  };
}

/* ------------------------------------------------------------------ *
 * Inventory
 * ------------------------------------------------------------------ */

function inventoryScreen(d: ScreenDeps): MenuModel {
  const rows: MenuRow[] = [];
  for (let i = 0; i < d.inventory.length; i++) {
    const slot = d.inventory[i];
    if (!slot || slot.count <= 0) continue;
    const ui = ITEM_UI[slot.kind];
    const label = slot.count > 1 ? `${ui.label} x${slot.count}` : ui.label;
    if (ui.usable) {
      rows.push({ kind: 'action', label, run: () => d.dispatch({ type: 'use-slot', index: i }) });
    } else {
      rows.push({ kind: 'info', label, value: 'CARRIED' });
    }
  }
  if (rows.length === 0) rows.push({ kind: 'info', label: 'Nothing carried.' });
  // `resume` rather than `show('hud')`, which is what this used to be.
  //
  // The pack can now be opened from the pause screen, because on a headset with
  // no buttons that is the only way in — and from there, putting the HUD back
  // up is not the same thing as starting the run again. `resume` is both, and
  // is what the pause screen's own RESUME row already sends; on the path this
  // row was written for, where the pack was opened straight from play, the two
  // are identical. Without it a player could close their pack into a building
  // that had stopped, with nothing left on screen to press.
  rows.push({ kind: 'action', label: 'CLOSE', run: () => d.dispatch({ type: 'resume' }) });

  return {
    key: 'inventory',
    title: 'INVENTORY',
    sub: 'CARRIED',
    rows,
  };
}

/* ------------------------------------------------------------------ *
 * Field notes
 * ------------------------------------------------------------------ */

function journalScreen(d: ScreenDeps): MenuModel {
  const rows: MenuRow[] = [];
  const selected = d.journal[d.journalIndex] ?? d.journal[0];
  for (let i = 0; i < d.journal.length; i++) {
    const note = d.journal[i]!;
    rows.push({ kind: 'action', label: note.title.toUpperCase(), run: () => d.selectJournal(i) });
  }
  if (rows.length === 0) rows.push({ kind: 'info', label: 'Nothing recorded yet.' });
  rows.push({ kind: 'action', label: 'CLOSE', run: () => d.closeJournal() });

  return {
    key: 'journal',
    title: 'FIELD NOTES',
    sub: selected ? selected.title : 'THIS RUN',
    body: selected ? [selected.body] : undefined,
    rows,
  };
}
